import { InvoiceType } from "@/lib/constants/invoiceStatus";
import * as Sentry from "@sentry/react";
import { AxiosError, type AxiosResponse } from "axios";
import { z } from "zod";
import { BACKEND_URL } from "../../lib/constants/constants";
import type {
  InvoiceData,
  ParsedAccountRecord,
  Transaction,
} from "../gql/types";
import axios from "./axiosConfig";

export const invoiceExtractionRetry = async (id: InvoiceData["invoiceId"]) => {
  const res = await axios.post(`${BACKEND_URL}/api/invoices/${id}/retry`);
  return res.data;
};

export const getInvoicePDFFromInvoiceId = async (invoiceId: string) => {
  try {
    const res = await axios.post(
      `${BACKEND_URL}/api/getInvoicePDFFromInvoiceId`,
      {
        invoiceId: invoiceId,
      },
    );
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const sendInvoice = async ({
  file,
  source,
  splitPdf,
  invoiceType = InvoiceType.PAYABLE,
}: {
  file: any;
  source: string;
  splitPdf?: boolean;
  invoiceType?: InvoiceType;
}) => {
  try {
    const formData = new FormData();

    // Appending the PDF file
    formData.append("invoice", file);
    formData.append("source", source);
    formData.append("fileName", file.name);
    formData.append("splitPdf", splitPdf ? "true" : "false");
    formData.append("invoiceType", invoiceType);

    const response = await axios.post(
      `${BACKEND_URL}/api/uploadPdf`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response;
  } catch (error: any) {
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const exportPDFs = async (invoiceIds: string[]) => {
  axios({
    method: "post",
    url: `${BACKEND_URL}/api/pdfInvoiceExport`,
    responseType: "blob", // Important to handle binary data
    data: {
      invoiceIds: invoiceIds,
    },
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download

      const { day, month, year, hours, minutes } = getCurrentDate();
      link.setAttribute(
        "download",
        `Rechnungen_${day}-${month}-${year}_${hours}${minutes}.zip`,
      );

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      console.log(error);
    });
};

export const exportEntriesToDATEV = async ({
  entityGroups,
  entityType,
  sessionId,
}: {
  entityGroups: Record<string, string[]>;
  entityType: "bank-transaction" | "invoice";
  sessionId: string;
}) => {
  await axios.post(
    `${BACKEND_URL}/api/datev/exports/buchungsdatenservice/buchungsdatenservice-bookings/new`,
    {
      entityGroups,
      entityType,
      sessionId,
    },
  );
};

export const exportSuppliersToDATEV = (supplierIds: string[]) => {
  return axios.post(
    `${BACKEND_URL}/api/datev/exports/buchungsdatenservice/suppliers/new`,
    {
      supplierIds,
    },
  );
};

export const exportDATEVXML = async (invoiceIds: string[]) => {
  return new Promise((resolve, reject) => {
    axios({
      method: "post",
      url: `${BACKEND_URL}/api/DUOExport`,
      responseType: "blob", // Important to handle binary data
      data: {
        invoiceIds: invoiceIds,
      },
    })
      .then((response) => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]));
        // Create a temporary anchor element and set its href to the blob URL
        const link = document.createElement("a");
        link.href = url;
        // Suggest a filename for the download
        const { day, month, year, hours, minutes } = getCurrentDate();

        const filename = `DATEV_${day}-${month}-${year}_${hours}${minutes}.zip`;
        link.setAttribute("download", filename);

        // Append the anchor to the body, click it, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        resolve("Export successful.");
      })
      .catch((error) => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(new Blob([error.response.data]));
        // Create a temporary anchor element and set its href to the blob URL
        const link = document.createElement("a");
        link.href = url;
        // Suggest a filename for the download
        const { day, month, year, hours, minutes } = getCurrentDate();

        const filename = `Fehler_${day}-${month}-${year}_${hours}${minutes}.txt`;
        link.setAttribute("download", filename);

        // Append the anchor to the body, click it, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        reject("Export failed.");
      });
  });
};

export const exportLineItemsCSV = async (invoiceIds: string[]) => {
  axios({
    method: "post",
    url: `${BACKEND_URL}/api/createCSVExport`,
    responseType: "blob", // Important to handle binary data
    data: {
      invoiceIds: invoiceIds,
      type: "lineItems",
    },
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download

      const { day, month, year, hours, minutes } = getCurrentDate();
      link.setAttribute(
        "download",
        `Einzelposten_${day}-${month}-${year}_${hours}${minutes}.csv`,
      );

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      console.log(error);
    });
};

export const exportInvoicesCSV = async (invoiceIds: string[]) => {
  axios({
    method: "post",
    url: `${BACKEND_URL}/api/createCSVExport`,
    responseType: "blob", // Important to handle binary data
    data: {
      invoiceIds: invoiceIds,
      type: "invoices",
    },
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download
      const { day, month, year, hours, minutes } = getCurrentDate();
      link.setAttribute(
        "download",
        `Rechnungen_${day}-${month}-${year}_${hours}${minutes}.csv`,
      );

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      console.log(error);
    });
};

export const exportSuppliersCSV = async () => {
  axios({
    method: "post",
    url: `${BACKEND_URL}/api/createCSVExport`,
    responseType: "blob", // Important to handle binary data
    data: {
      type: "suppliers",
    },
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download
      const { day, month, year, hours, minutes } = getCurrentDate();
      link.setAttribute(
        "download",
        `Kreditoren_${day}-${month}-${year}_${hours}${minutes}.csv`,
      );

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      console.log(error);
    });
};

export enum DatevSupplierExportType {
  NEW = "new",
  UPDATED = "updated",
  ALL = "all",
  NEW_AND_UPDATED = "newAndUpdated",
}

export const datevSupplierExport = async (supplierIds: string[]) => {
  return axios({
    method: "post",
    url: `${BACKEND_URL}/api/datevSupplierExport`,
    responseType: "blob", // Important to handle binary data
    data: {
      supplierIds,
    },
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download
      const { day, month, year, hours, minutes } = getCurrentDate();
      link.setAttribute(
        "download",
        `Kreditoren_${day}-${month}-${year}_${hours}${minutes}.csv`,
      );

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([error.response.data]));
      // Create a temporary anchor element and set its href to the blob URL
      const link = document.createElement("a");
      link.href = url;
      // Suggest a filename for the download
      const { day, month, year, hours, minutes } = getCurrentDate();

      const filename = `Fehler_${day}-${month}-${year}_${hours}${minutes}.txt`;
      link.setAttribute("download", filename);

      // Append the anchor to the body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
};

function getCurrentDate() {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // getMonth() is zero-based; add 1 to make it 1-indexed
  const day = currentDate.getDate().toString().padStart(2, "0");
  const hours = currentDate.getHours().toString().padStart(2, "0");
  const minutes = currentDate.getMinutes().toString().padStart(2, "0");
  return { day, month, year, hours, minutes };
}

export const getInvoiceGroupsByMonth = async (invoiceIds: string[]) => {
  const response = await axios.post(
    `${BACKEND_URL}/api/datev/exports/buchungsdatenservice/invoices/group-by-month`,
    {
      invoiceIds,
    },
  );
  return response.data;
};

export const exportInvoicesByMonth = async (
  invoiceGroups: Record<string, string[]>,
  sessionId: string,
) => {
  const response = await axios.post(
    `${BACKEND_URL}/api/datev/exports/buchungsdatenservice/invoices/export-by-month`,
    {
      invoiceGroups,
      sessionId,
    },
  );
  return response.data;
};

export const generateDatevFormatFiles = async ({
  invoiceGroups,
  entityGroups,
  entityType,
}: {
  invoiceGroups?: Record<string, string[]>;
  entityGroups?: Record<string, string[]>;
  entityType?: "bank-transaction" | "invoice";
}) => {
  const response = await axios({
    method: "post",
    url: `${BACKEND_URL}/api/datev/exports/buchungsdatenservice/datev-formats`,
    responseType: "blob",
    data: {
      invoiceGroups,
      entityGroups,
      entityType,
    },
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const { day, month, year, hours, minutes } = getCurrentDate();
  link.setAttribute(
    "download",
    `DATEV_Formate_${day}-${month}-${year}_${hours}${minutes}.zip`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return response.data;
};

export const parseCostCenterFile = async (file: File, companyName: string) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyName", companyName);

    const response = await axios.post(
      `${BACKEND_URL}/api/cost-center-file/parse`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error(error?.response?.data);
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const parseAccountFile = async (
  file: File,
  skrType: "skr03" | "skr04",
  companyName?: string,
): Promise<{
  data: ParsedAccountRecord[];
  accountLength: 4 | 5 | 6;
  numberOfAccountsBeforeDeletion: number;
  error: string;
}> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("skrType", skrType);
    if (companyName) {
      formData.append("companyName", companyName);
    }

    const response = await axios.post(
      `${BACKEND_URL}/api/accounts-file/parse`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error(error?.response?.data);
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const parseDebtorCreditorFile = async (
  file: File,
  companyName: string,
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyName", companyName);

    const response = await axios.post(
      `${BACKEND_URL}/api/debtor-creditor-file/parse`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const responseSchema = z.object({
      debtors: z.array(
        z.object({
          accountNumber: z.number(),
          name: z.string(),
          vatId: z.string().optional(),
          customerNumber: z.string().optional(),
        }),
      ),
      creditors: z.array(
        z.object({
          accountNumber: z.number(),
          name: z.string(),
          vatId: z.string().optional(),
          customerNumber: z.string().optional(),
        }),
      ),
    });

    return responseSchema.parse(response.data.data);
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

export const parseCounterpartiesForImport = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${BACKEND_URL}/api/counterparties/parse`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const counterpartySchema = z.object({
      accountNumber: z.number(),
      name: z.string(),
      vatId: z.string().optional(),
      customerNumber: z.string().optional(),
    });

    const responseSchema = z.object({
      existingCreditorsCount: z.number(),
      newCreditors: z.array(counterpartySchema),
      existingDebtorsCount: z.number(),
      newDebtors: z.array(counterpartySchema),
    });

    return responseSchema.parse(response.data);
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

export const importCounterparties = async (data: {
  creditors: Array<{
    accountNumber: number;
    name: string;
    vatId?: string;
    customerNumber?: string;
  }>;
  debtors: Array<{
    accountNumber: number;
    name: string;
    vatId?: string;
    customerNumber?: string;
  }>;
}) => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/api/counterparties/import`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

type ReauthBankConnectionResponse = {
  webformUrl: string | null;
};

export const reauthenticateBankConnection = async (
  bankConnectionId: string,
  companyId: string,
  forceFintsInterface = false,
): Promise<AxiosResponse<ReauthBankConnectionResponse>> => {
  try {
    const response = await axios.post<ReauthBankConnectionResponse>(
      `${BACKEND_URL}/api/reauthenticate-bank-connection`,
      {
        bankConnectionId,
        companyId,
        forceFintsInterface,
      },
    );
    return response;
  } catch (error: any) {
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

const AddBankAccountResponseSchema = z.object({
  webformUrl: z.string().nullable(),
});

type AddBankAccountResponse = z.infer<typeof AddBankAccountResponseSchema>;

export const addBankAccount = async (
  bankConnectionId: string,
  companyId: string,
): Promise<AxiosResponse<AddBankAccountResponse>> => {
  try {
    const response = await axios.post<AddBankAccountResponse>(
      `${BACKEND_URL}/api/add-bank-account`,
      {
        bankConnectionId,
        companyId,
      },
    );

    // Validate response with Zod
    const validatedData = AddBankAccountResponseSchema.parse(response.data);
    return {
      ...response,
      data: validatedData,
    };
  } catch (error: any) {
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const syncBankAccountsAndImport = async () => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/api/sync-bank-accounts-and-import`,
    );
    return response.data;
  } catch (error: any) {
    console.error(error);
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const parseBankTransactionsFile = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${BACKEND_URL}/api/bank-transactions/parse`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error(error?.response?.data ?? error);
    Sentry.captureException(error);
    return error?.response ?? { status: 400 };
  }
};

export const checkTransactionsDuplicates = async (
  transactions: Partial<Transaction>[],
): Promise<{ nonDuplicates: Transaction[]; duplicates: Transaction[] }> => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/api/bank-transactions/check-duplicates`,
      { transactions },
    );
    return response.data;
  } catch (error: any) {
    console.error(error?.response?.data ?? error);
    Sentry.captureException(error);
    throw error;
  }
};

export const createThumbnailsForPDF = async (
  file: File,
  signal?: AbortSignal,
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${BACKEND_URL}/api/createThumbnailsForPDF`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        signal,
      },
    );

    return response.data as { imageUrls: string[]; format: string };
  } catch (error: any) {
    Sentry.captureException(error);
    throw error?.response ?? error;
  }
};

export const getSmtpProvider = async (
  email: string,
): Promise<{ smtpProvider: string | null }> => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/smtp-provider`, {
      params: {
        email,
      },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    if (error instanceof AxiosError) {
      throw error.response ?? error;
    }
    throw error;
  }
};
